import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AreaSelect from '../components/AreaSelect';
import { routeApi, dashboardApi } from '../api/mockApi';

const VEHICLE_TYPES = ['Saloon Car', 'Wheelchair Minibus', 'Black Cab', '7 Seater MPV', '9 Seater MPV', 'W/C Taxi'];
const ROUTE_STATUSES = ['ON ROUTE', 'OFF ROUTE'];

const EMPTY_FORM = {
  name: '', area: '', postCode: '', vehicleRequired: '',
  routeStatus: 'OFF ROUTE', notes: '',
};

const EMPTY_FILTERS = { name: '', area: '', vehicle: '', routeStatus: '', assigned: '' };

const RoutesPage = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [nameInput, setNameInput] = useState('');
  const [areaOptions, setAreaOptions] = useState([]);
  const navigate = useNavigate();

  // Debounce the free-text name filter (350ms) so we don't hit the API per keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => (prev.name === nameInput ? prev : { ...prev, name: nameInput }));
    }, 350);
    return () => clearTimeout(timer);
  }, [nameInput]);

  const loadRoutes = useCallback(async () => {
    setLoading(true);
    const res = await routeApi.getAll(filters);
    if (res.success) setRoutes(res.data || []);
    setLoading(false);
    setFirstLoad(false);
  }, [filters]);

  useEffect(() => { loadRoutes(); }, [loadRoutes]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      const res = await dashboardApi.getFilters();
      if (res.success) setAreaOptions(res.data?.areas || []);
    };
    loadFilterOptions();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    const res = await routeApi.create(form);
    setSaving(false);
    if (res.success) {
      setShowModal(false);
      setForm(EMPTY_FORM);
      loadRoutes();
    } else {
      window.alert(res.error || 'Could not create route');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this route?')) {
      await routeApi.delete(id);
      loadRoutes();
    }
  };

  const clearFilters = () => {
    setNameInput('');
    setFilters(EMPTY_FILTERS);
  };

  const hasFilters = Object.values(filters).some(v => v !== '');

  // Areas come from the dashboard filter endpoint, topped up with whatever the loaded routes use
  const areas = Array.from(new Set([
    ...areaOptions,
    ...routes.map(r => r.area).filter(Boolean),
  ])).sort((a, b) => a.localeCompare(b));

  if (firstLoad) return <div className="loading">Loading routes...</div>;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Routes ({routes.length})</h1>
        <button className="btn btn-gold" onClick={() => setShowModal(true)}>+ Add Route</button>
      </div>

      <div className="filters-bar">
        <div className="filter-field">
          <label>Route Name</label>
          <input
            placeholder="Search by name"
            value={nameInput}
            onChange={e => setNameInput(e.target.value)}
          />
        </div>
        <div className="filter-field">
          <label>Area</label>
          <select value={filters.area} onChange={e => setFilters({ ...filters, area: e.target.value })}>
            <option value="">All areas</option>
            {areas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label>Vehicle</label>
          <select value={filters.vehicle} onChange={e => setFilters({ ...filters, vehicle: e.target.value })}>
            <option value="">All vehicles</option>
            {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label>Route Status</label>
          <select value={filters.routeStatus} onChange={e => setFilters({ ...filters, routeStatus: e.target.value })}>
            <option value="">All statuses</option>
            {ROUTE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="filter-field">
          <label>Assigned</label>
          <select value={filters.assigned} onChange={e => setFilters({ ...filters, assigned: e.target.value })}>
            <option value="">All</option>
            <option value="yes">Assigned</option>
            <option value="no">Unassigned</option>
          </select>
        </div>
        <div className="filters-actions">
          <button className="btn btn-outline btn-sm" onClick={clearFilters} disabled={!hasFilters && !nameInput}>
            Clear filters
          </button>
          <span className="filter-summary">
            {loading ? 'Loading...' : `${routes.length} route${routes.length === 1 ? '' : 's'}${hasFilters ? ' (filtered)' : ''}`}
          </span>
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Area</th>
            <th>Post Code</th>
            <th>Vehicle Required</th>
            <th>Driver</th>
            <th>PA</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {routes.map(r => (
            <tr key={r.id}>
              <td style={{ cursor: 'pointer', color: '#DBAD17' }} onClick={() => navigate(`/routes/${r.id}`)}>
                {r.name || 'N/A'}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>{r.area || 'N/A'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{r.postCode || 'N/A'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>{r.vehicleRequired || 'N/A'}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {r.driverName ? r.driverName : <span className="pill">Unassigned</span>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {r.escortName ? r.escortName : <span className="pill">Unassigned</span>}
              </td>
              <td>
                <span className={`status-badge ${r.routeStatus === 'ON ROUTE' ? 'on-route' : 'off-route'}`}>
                  <span className="dot"></span>
                  {r.routeStatus}
                </span>
              </td>
              <td>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => navigate(`/routes/${r.id}`)}>View</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(r.id)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
          {!routes.length && (
            <tr>
              <td colSpan={8}>
                <div className="empty-state">
                  {hasFilters ? 'No routes match these filters.' : 'No routes yet — add one to get started.'}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Add New Route</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Area</label>
                <AreaSelect
                  value={form.area || ''}
                  onChange={v => setForm({ ...form, area: v })}
                  blankLabel="Detect from post code"
                />
              </div>
              <div className="form-group">
                <label>Post Code</label>
                <input value={form.postCode} onChange={e => setForm({ ...form, postCode: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Vehicle Required</label>
                <select value={form.vehicleRequired} onChange={e => setForm({ ...form, vehicleRequired: e.target.value })}>
                  <option value="">Select vehicle</option>
                  {VEHICLE_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Route Status</label>
                <select value={form.routeStatus} onChange={e => setForm({ ...form, routeStatus: e.target.value })}>
                  {ROUTE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-gold" disabled={saving}>{saving ? 'Adding...' : 'Add Route'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutesPage;
